/**
 * Pending Queue Section
 * Shows all pending approvals in FIFO order (oldest first)
 */

import { useState } from 'react';
import type { TeacherQueueItem, Quest } from '@/types/tisa-coins';

interface Props {
  queue: TeacherQueueItem[];
  quests: Quest[];
  onApprove: (item: TeacherQueueItem) => void;
  onReject: (item: TeacherQueueItem, note: string) => void;
}

export function PendingQueueSection({ queue, quests, onApprove, onReject }: Props) {
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const handleRejectClick = (id: string) => {
    setRejectingId(id);
    setRejectNote('');
  };

  const handleRejectConfirm = (item: TeacherQueueItem) => {
    onReject(item, rejectNote);
    setRejectingId(null);
    setRejectNote('');
  };

  return (
    <div className="bg-gray-900/50 border-2 border-crimson/30 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-cinzel font-bold text-crimson">
          ⏰ Pending Approvals ({queue.length})
        </h2>
        <p className="text-sm text-gray-400">Oldest first (FIFO)</p>
      </div>

      {queue.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-6xl mb-4">🎉</p>
          <p className="text-xl text-gray-300 font-cinzel mb-2">All caught up!</p>
          <p className="text-gray-400">No pending approvals</p>
        </div>
      ) : (
        <div className="space-y-4">
          {queue.map((item) => {
            const quest = quests.find(q => q.id === item.metadata.quest_id);
            const isRejecting = rejectingId === item.id;

            return (
              <div
                key={item.id}
                className="bg-black/30 border-2 border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple to-pink flex items-center justify-center text-2xl font-bold text-white">
                      {item.user_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-cinzel font-bold text-white text-lg">{item.user_name}</p>
                      <p className="text-sm text-gray-400">{item.user_class}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      item.type === 'quest' ? 'bg-emerald/20 text-emerald' :
                      item.type === 'coin' ? 'bg-gold/20 text-gold' :
                      'bg-purple/20 text-purple'
                    }`}>
                      {item.type.toUpperCase()}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Quest Details */}
                {item.type === 'quest' && quest && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-cinzel font-bold text-gold">{quest.title}</h3>
                      <span className="text-emerald font-orbitron font-bold">+{quest.xp_reward} XP</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">{quest.description}</p>

                    {/* Evidence */}
                    {item.metadata.evidence && (
                      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                        <p className="text-xs text-gray-400 mb-2">📸 Evidence Submitted:</p>

                        {/* Photos */}
                        {item.metadata.evidence.photos && item.metadata.evidence.photos.length > 0 && (
                          <div className="flex gap-2 mb-3 overflow-x-auto">
                            {item.metadata.evidence.photos.map((photo: string, i: number) => (
                              <img
                                key={i}
                                src={photo}
                                alt={`Evidence ${i + 1}`}
                                className="w-24 h-24 object-cover rounded-lg border-2 border-gold/30 hover:border-gold cursor-pointer"
                                onClick={() => window.open(photo, '_blank')}
                              />
                            ))}
                          </div>
                        )}

                        {/* Text */}
                        {item.metadata.evidence.text && (
                          <div className="bg-black/30 rounded p-3 border border-gray-700/30">
                            <p className="text-xs text-gray-400 mb-1">Student Reflection:</p>
                            <p className="text-sm text-white">{item.metadata.evidence.text}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Coin Request Details */}
                {item.type === 'coin' && (
                  <div className="mb-4 bg-gold/10 rounded-lg p-4 border border-gold/30">
                    <p className="text-gold font-orbitron font-bold text-2xl mb-2">
                      {item.metadata.amount} coins
                    </p>
                    <p className="text-sm text-gray-300 mb-1">Category: {item.metadata.category}</p>
                    {item.metadata.proof && (
                      <p className="text-xs text-gray-400">Proof: {item.metadata.proof}</p>
                    )}
                  </div>
                )}

                {/* Actions */}
                {!isRejecting ? (
                  <div className="flex gap-3">
                    <button
                      onClick={() => onApprove(item)}
                      className="flex-1 px-6 py-3 bg-emerald text-white rounded-lg font-bold hover:bg-emerald/80 transition-all"
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => handleRejectClick(item.id)}
                      className="flex-1 px-6 py-3 bg-crimson/20 text-crimson border-2 border-crimson rounded-lg font-bold hover:bg-crimson/30 transition-all"
                    >
                      ❌ Reject
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <textarea
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      placeholder="Why are you rejecting? (optional feedback for student)"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white resize-none focus:border-crimson focus:outline-none"
                      rows={3}
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleRejectConfirm(item)}
                        className="flex-1 px-6 py-3 bg-crimson text-white rounded-lg font-bold hover:bg-crimson/80"
                      >
                        Confirm Reject
                      </button>
                      <button
                        onClick={() => setRejectingId(null)}
                        className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
